/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { ItemWithUploadUrls } from '@app/app/shared/models/ItemWithUploadUrl';
import { Page } from '@app/app/shared/models/page';
import { Observable, of } from 'rxjs';
import {
  AssessmentSummary,
  AssessmentTextAnalysisStatus,
  CreateReportFeedbackSectionRequest,
  CreateReportResourceSectionRequest,
  DiagnosticDescriptionQuestionResponse,
  DiagnosticLogic,
  DiagnosticQuestionDistribution,
  DiagnosticQuestionRequest,
  DiagnosticQuestionResponse,
  Diagnostics,
  DiagnosticsRetake,
  FeedbackProviderSettingRequest,
  FeedbackProviderSettings,
  FeedbackProviderUser,
  FeedbackRecipientSummaryEntry,
  GeneratedReport,
  LearningGoalIconRequest,
  LogicGoal,
  LogicGoalLevel,
  MyPagesAssessmentsResponse,
  QuestionEditor,
  QuestionResponses,
  Report,
  ReportMode,
  SelectedFeedbackProvidersRequest,
  ShowReportLogoRequest,
  UpdateReportFeedbackProvidersRequest,
  UpdateReportFeedbackQuestionRequest,
  UpdateReportFeedbackStatsRequest,
  UpdateReportResourceSectionRequest,
  UpdateReportSectionResponse,
  UpdateReportTitleRequest,
} from '../../../shared/models';
import { ObservableResult } from '../../../shared/store';
import { DiagnosticsDataService } from './data.service';

/* eslint-disable max-len */
@Injectable()
export class MockDiagnosticsDataService implements DiagnosticsDataService {
  private dummyQuestionResponse: QuestionResponses = {
    uid: 'string',
    responses: [],
    feedbackResponses: [],
    version: 1,
    status: 'IN_PROGRESS',
  };

  private dummyAssessmentSummary: AssessmentSummary = {
    retake: true,
    lastVersion: 10,
    completed: true,
    inProgress: false,
    questionResponseId: '123Aas7',
    feedbackSummary: {
      hasFeedback: false,
      feedbackFor: 'Test',
    },
  };

  createQuestion(
    assessmentUid: string,
    questionData: DiagnosticQuestionRequest,
    languageCode?: string,
  ): ObservableResult<DiagnosticQuestionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateQuestion(
    assessmentUid: string,
    questionUid: string,
    questionData: DiagnosticQuestionRequest,
    languageCode?: string,
  ): ObservableResult<DiagnosticQuestionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateQuestionDescription(
    assessmentUid: string,
    questionUid: string,
    questionData: QuestionEditor,
    languageCode?: string,
  ): ObservableResult<DiagnosticDescriptionQuestionResponse> {
    return ObservableResult.ofSuccess();
  }

  copyQuestion(assessmentUid: string, questionUid: string): ObservableResult<DiagnosticQuestionResponse> {
    return ObservableResult.ofSuccess();
  }

  deleteQuestion(assessmentUid: string, questionUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  reorderQuestions(assessmentUid: string, questions: string[]): ObservableResult<DiagnosticQuestionResponse[]> {
    return ObservableResult.ofSuccess();
  }

  getDiagnosticsLogicGoals(assessmentUid: string): ObservableResult<DiagnosticLogic[]> {
    return ObservableResult.ofSuccess();
  }

  updateLogicGoal(assessmentUid: string, goalUid: string, goalData: LogicGoal): ObservableResult<Diagnostics> {
    return ObservableResult.ofSuccess();
  }

  updateLogicGoalLevel(assessmentUid: string, goalUid: string, levelUid: string, levelData: LogicGoalLevel): ObservableResult<Diagnostics> {
    return ObservableResult.ofSuccess();
  }

  updateLearningGoalIcon(
    assessmentUid: string,
    goalUid: string,
    type: 'MAIN' | 'MAX' | 'MIN',
    iconData: LearningGoalIconRequest,
    file: File,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateAssessmentChartIcon(
    assessmentUid: string,
    sectionUid: string,
    dynamicContentUid: string,
    type: 'MAX' | 'MIN',
    iconData: LearningGoalIconRequest,
    file: File,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  removeLearningGoalIcon(assessmentUid: string, goalUid: string, type: 'MAIN' | 'MAX' | 'MIN'): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  removeAssessmentChartIcon(
    assessmentUid: string,
    sectionUid: string,
    dynamicContentUid: string,
    type: 'MAIN' | 'MAX' | 'MIN',
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  addQuestionResponse(): ObservableResult<ItemWithUploadUrls<QuestionResponses>> {
    return ObservableResult.ofSuccess();
  }

  getQuestionResponses(assessmentUid: string, feedbackFor?: string): ObservableResult<QuestionResponses> {
    return ObservableResult.ofSuccess(this.dummyQuestionResponse);
  }

  getQuestionDistributions(assessmentUid: string): ObservableResult<DiagnosticQuestionDistribution[]> {
    return ObservableResult.ofSuccess([]);
  }

  updateDiagnosticsRetake(assessmentUid: string, request: DiagnosticsRetake): ObservableResult<QuestionResponses> {
    return ObservableResult.ofSuccess();
  }

  randomizeQuestions(assessmentUid: string): ObservableResult<QuestionResponses> {
    return ObservableResult.ofSuccess();
  }

  deleteRandomizeQuestions(assessmentUid: string): ObservableResult<QuestionResponses> {
    return ObservableResult.ofSuccess();
  }

  getAssessmentSummary(
    assessmentUid: string,
    languageCode?: string,
    feedbackFor?: string,
    targetUserUid?: string,
  ): ObservableResult<AssessmentSummary> {
    return ObservableResult.ofSuccess(this.dummyAssessmentSummary);
  }

  getAssessmentSummaryAdmins(
    assessmentUid: string,
    userUid: string,
    languageCode?: string,
    feedbackFor?: string,
  ): ObservableResult<AssessmentSummary> {
    return ObservableResult.ofSuccess(this.dummyAssessmentSummary);
  }

  getDiagnosticsQuestions(assessmentUid: string, languageCode?: string): ObservableResult<DiagnosticQuestionResponse[]> {
    return ObservableResult.ofSuccess();
  }

  addSelectedFeedbackProviders(
    assessmentUid: string,
    questionResponseUid: string,
    request: SelectedFeedbackProvidersRequest,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateFeedbackProvidersSettings(
    assessmentUid: string,
    request: FeedbackProviderSettingRequest,
  ): ObservableResult<FeedbackProviderSettings> {
    return ObservableResult.ofSuccess();
  }

  searchFeedbackProviders(
    assessmentUid: string,
    questionResponseUid: string,
    term: string,
    page: number,
    size: number,
  ): ObservableResult<Page<FeedbackProviderUser>> {
    return ObservableResult.ofSuccess();
  }

  /** Reports. */

  createReportStandardSection(): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  createReportStandardChartSection(): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  createReportConditionalSection(): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  createReportResourceSection(
    assessmentUid: string,
    request: CreateReportResourceSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  createReportFeedbackSection(
    assessmentUid: string,
    request: CreateReportFeedbackSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportTitle(assessmentUid: string, request: UpdateReportTitleRequest, languageCode?: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateReportStandardSection(): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportConditionalSection(): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportResourceSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportResourceSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportFeedbackQuestionSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackQuestionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportFeedbackStatsSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackStatsRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  updateReportFeedbackProvidersSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackProvidersRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse> {
    return ObservableResult.ofSuccess();
  }

  deleteReportSection(): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  reorderReportSections(): ObservableResult<Report> {
    return ObservableResult.ofSuccess();
  }

  getReport(
    assessmentUid: string,
    responsesUid: string,
    fps?: string,
    languageCode?: string,
    targetUserUid?: string,
  ): ObservableResult<GeneratedReport> {
    return ObservableResult.ofSuccess();
  }

  showReportTitle(assessmentUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  hideReportTitle(assessmentUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  showHideReportOrgLogo(assessmentUid: string, request: ShowReportLogoRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateReportMode(assessmentUid: string, mode: ReportMode): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  addReportResourceTag(assessmentUid: string, sectionUid: string, tagUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getAssessmentsResponses(): ObservableResult<MyPagesAssessmentsResponse[]> {
    return ObservableResult.ofSuccess();
  }

  getSpecificRecipientFeedbacksSummary(recipientUid: string): ObservableResult<FeedbackRecipientSummaryEntry> {
    return ObservableResult.ofSuccess();
  }

  deleteReportResourceTag(assessmentUid: string, sectionUid: string, tagUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getTextAnalysisStatus(_assessmentUid: string): Observable<AssessmentTextAnalysisStatus> {
    return of({
      hasTextAnalysisEnabledQuestions: false,
      contextExists: false,
      totalTextResponses: 0,
      submittedToAiCount: 0,
      pendingCount: 0,
      classifiedCount: 0,
      needsResync: false,
      phase: 'COMPLETED',
    });
  }

  resyncTextAnalysis(_assessmentUid: string): Observable<void> {
    return of(undefined);
  }
}
